import * as Sentry from '@sentry/react'

const DEFAULT_REPLAY_SESSION_SAMPLE_RATE = 0.1
const DEFAULT_REPLAY_ON_ERROR_SAMPLE_RATE = 1.0

// High-frequency, non-actionable errors that would otherwise consume the
// error-replay quota (replaysOnErrorSampleRate). Dropping them here prevents
// both the error event and its associated session replay from being sent.
// Anchored patterns avoid Sentry's default substring matching, which would
// also drop unrelated errors that merely contain these tokens (e.g. a future
// "db_connection_timeout: pool exhausted").
const IGNORED_ERRORS: (string | RegExp)[] = [
  // Transient WebSocket timeouts on the realtime dashboard connection.
  // Emitted as `WebSocket: connection_timeout` via captureMessage in api.websocket.ts.
  /^WebSocket: connection_timeout$/,
  // Browser IndexedDB eviction (tab close / multi-tab / storage pressure).
  // Real message has trailing recovery text, so anchor at the start only.
  /^Connection to Indexed Database server lost/i,
]

// Replay never captures request/response bodies or headers unless an allow-list
// is configured, but this deny-list guarantees sensitive endpoints stay excluded
// if network detail capture is ever enabled, and documents that intent.
const REPLAY_NETWORK_DETAIL_DENY_URLS: (string | RegExp)[] = [
  /\/auth(\/|$)/i,
  /token/i,
  /\/otp(\/|$)/i,
  /\/sso(\/|$)/i,
  /login/i,
]

function getDefaultReplaySessionSampleRate(environment: string): number {
  return environment === 'development' ? 1.0 : DEFAULT_REPLAY_SESSION_SAMPLE_RATE
}

function parseReplaySampleRate(value: string | undefined, fallback: number, envName: string): number {
  if (value === undefined || value.trim() === '') {
    return fallback
  }

  const parsedValue = Number(value)

  if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 1) {
    console.warn(`${envName} must be a number between 0 and 1. Falling back to ${fallback}.`)
    return fallback
  }

  return parsedValue
}

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  const environment = import.meta.env.VITE_ENV || 'development'

  // Add debug logging in development
  if (environment === 'development') {
    console.log('🔍 Sentry beforeSend:', { // eslint-disable-line no-console
      eventId: event.event_id,
      message: event.message || (event.exception?.values?.[0]?.value),
      tags: event.tags,
      extra: event.extra,
      timestamp: new Date().toISOString(),
    })
  }

  if (event.user) {
    const { email: _email, username: _username, ip_address: _ipAddress, id: _id, ...userWithoutPII } = event.user
    event.user = userWithoutPII
  }

  if (event.request?.url) {
    const url = new URL(event.request.url)
    url.search = ''
    event.request.url = url.toString()
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
      ...breadcrumb,
      data: undefined,
    }))
  }

  return event
}

export function initSentry() {
  const environment = import.meta.env.VITE_ENV || 'development'
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    console.warn('Sentry DSN not provided. Sentry will not be initialized.')
    return
  }

  const replaysSessionSampleRate = parseReplaySampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    getDefaultReplaySessionSampleRate(environment),
    'VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE',
  )
  const replaysOnErrorSampleRate = parseReplaySampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    DEFAULT_REPLAY_ON_ERROR_SAMPLE_RATE,
    'VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE',
  )

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailDenyUrls: REPLAY_NETWORK_DETAIL_DENY_URLS,
      }),
    ],
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    ignoreErrors: IGNORED_ERRORS,
    beforeSend,
    sendDefaultPii: false,
    // Disable rate limiting in development for testing
    ...(environment === 'development' && {
      // Allow unlimited events in development
      maxBreadcrumbs: 100,
      // Custom transport options to bypass rate limiting
      transportOptions: {
        // Remove default rate limiting
      },
    }),
    release: import.meta.env.VITE_VERSION_NUMBER || __APP_VERSION__ || 'unknown',
    beforeSendTransaction: (transaction) => {
      if (transaction.transaction?.includes('user') || transaction.transaction?.includes('profile')) {
        return null
      }
      return transaction
    },
    initialScope: {
      tags: {
        component: 'hello-screens-client',
      },
    },
  })

  // Override rate limiting for development
  if (environment === 'development') {
    const client = Sentry.getClient()
    if (client) {
      // Override the client's rate limiter for development
      ;(client as any)._rateLimiter = {
        isRateLimited: () => false,
        recordSuccess: () => {},
        recordError: () => {},
      }
      console.log('🚀 Sentry rate limiting disabled for development') // eslint-disable-line no-console
    }
  }

  // eslint-disable-next-line no-console
  console.log(`Sentry initialized for environment: ${environment}`)
}

export function captureException(error: Error, context?: Record<string, any>) {
  const environment = import.meta.env.VITE_ENV || 'development'

  Sentry.withScope((scope) => {
    if (context) {
      const piiKeys = ['email', 'username', 'userid', 'id', 'phone', 'address']

      const sanitizedContext = Object.fromEntries(
        Object.entries(context).filter(([key]) => {
          const isFiltered = piiKeys.includes(key.toLowerCase())
          return !isFiltered
        }),
      )

      scope.setContext('additional', sanitizedContext)
    }

    // In development, bypass deduplication with unique tags
    if (environment === 'development') {
      scope.setTag('dev_timestamp', Date.now().toString())
      scope.setTag('dev_random', Math.random().toString(36).substring(2, 8))
    }

    Sentry.captureException(error)
  })
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', extra?: Record<string, any>) {
  Sentry.withScope((scope) => {
    if (extra) {
      scope.setExtra('details', extra)
    }
    Sentry.captureMessage(message, level)
  })
}
